/**
 * Browser Use Global Tool
 *
 * Launches a virtual desktop session so users can watch Prest0n
 * browse the web in real time. Integrates with the browser-service
 * backend (Xvfb + Playwright + noVNC).
 *
 * Trigger: "browse [query]", "use the computer to [task]",
 *          "research online [topic]", "go to [url]"
 */

import type {
  GlobalToolDefinition,
  StepContext,
  StepResult,
  TriggerMatch,
  ToolTheme,
} from '../types';
import { getAuth } from 'firebase/auth';
import { mintDeepLinkUrl } from './browserDeepLinkClient';
import { getInlineBrowserTileFrame } from './browserSessionFrames';
import { hasNearbyNegation } from '../triggerHelpers';
import {
  getBrowserServiceBaseUrl,
  getBrowserServiceAuthHeaders,
} from '../../browserService/baseUrl';

export const BUTTON_IDS = {
  VIEW_DESKTOP: 'browser-use-view-desktop',
  STOP_SESSION: 'browser-use-stop-session',
  // Phase 2 — inline form-aware pause UI
  OPEN_DEEP_LINK: 'browser-use-open-deep-link',
  ANSWER_INPUT: 'browser-use-answer-input',
  SUBMIT_ANSWER: 'browser-use-submit-answer',
  LANG_TOGGLE: 'browser-use-lang-toggle',
} as const;

const BROWSER_USE_THEME: ToolTheme = {
  accent: '#b45309',      // Amber 700 (matches brand gold)
  accentBg: '#fef3c7',    // Amber 100
  border: 'var(--brand-gold)',      // Prest0 gold
  subtitle: '#78716c',    // Stone 500
  muted: '#a8a29e',       // Stone 400
};

// ============================================================================
// Tool State
// ============================================================================

interface BrowserUseState {
  goal: string;
  sessionId: string | null;
  status: 'idle' | 'starting' | 'running' | 'complete' | 'error';
  /** Phase 2: form-aware pause snapshot for inline clarifying-question UI. */
  pendingFieldId?: string | null;
  pendingClarifyingQuestion?: { en: string; es: string } | null;
}

/**
 * Render the Phase 2 inline browser tile: live thumbnail + signed deep-link
 * to the AgentActivity page + form-aware pause fallback (clarifying question
 * with EN/ES toggle and inline answer input).
 *
 * Returns an HTML string suitable for `stream.finalize({ html })`.
 */
export function renderBrowserTileHtml(opts: {
  sessionId: string;
  goal: string;
  signedUrl?: string | null;
  thumbnailDataUrl?: string | null;
  pendingFieldId?: string | null;
  pendingClarifyingQuestion?: { en: string; es: string } | null;
  locale?: 'en' | 'es';
}): string {
  const {
    sessionId,
    goal,
    signedUrl,
    thumbnailDataUrl,
    pendingFieldId,
    pendingClarifyingQuestion,
    locale = 'en',
  } = opts;

  const fallbackUrl =
    signedUrl ?? `/agent-activity?session=${encodeURIComponent(sessionId)}`;

  const thumbBlock = thumbnailDataUrl
    ? `<a href="${fallbackUrl}" target="_blank" rel="noopener" data-button-id="${BUTTON_IDS.OPEN_DEEP_LINK}" style="display:block;border-radius:10px;overflow:hidden;border:1px solid var(--brand-line);margin-bottom:12px;">
        <img src="${thumbnailDataUrl}" alt="Live browser preview" style="display:block;width:100%;max-height:240px;object-fit:cover;" />
      </a>`
    : `<div style="background:#f8f8f6;border:1px solid var(--brand-line);border-radius:10px;padding:24px;text-align:center;color:var(--brand-text-muted);font-size:0.78rem;margin-bottom:12px;">live preview connecting…</div>`;

  const clarifyBlock = pendingFieldId && pendingClarifyingQuestion
    ? `<div style="background:#fff7ed;border:1px solid #fdba74;border-radius:10px;padding:12px;margin-bottom:12px;">
        <div style="font-size:0.72rem;color:#9a3412;font-weight:600;text-transform:lowercase;letter-spacing:0.04em;margin-bottom:6px;">⚠️ Attorney review — Prest0n needs more info</div>
        <div data-i18n="clarify" data-en="${escapeAttr(
          pendingClarifyingQuestion.en
        )}" data-es="${escapeAttr(
          pendingClarifyingQuestion.es
        )}" style="font-size:0.92rem;color:var(--brand-text);line-height:1.45;margin-bottom:10px;">${
          locale === 'es'
            ? pendingClarifyingQuestion.es
            : pendingClarifyingQuestion.en
        }</div>
        <div style="display:flex;gap:8px;align-items:center;">
          <input type="text" data-button-id="${BUTTON_IDS.ANSWER_INPUT}" data-session-id="${sessionId}" data-field-id="${pendingFieldId}" placeholder="${
          locale === 'es' ? 'Su respuesta…' : 'Your answer…'
        }" style="flex:1;border:1px solid var(--brand-line);border-radius:8px;padding:7px 10px;font-size:0.85rem;" />
          <button data-button-id="${BUTTON_IDS.SUBMIT_ANSWER}" data-session-id="${sessionId}" data-field-id="${pendingFieldId}" style="background:var(--brand-gold);color: var(--brand-charcoal);border:none;border-radius:8px;padding:7px 14px;font-size:0.82rem;font-weight:600;cursor:pointer;">${
          locale === 'es' ? 'enviar' : 'send'
        }</button>
        </div>
        <div style="display:flex;gap:6px;font-size:0.72rem;margin-top:8px;color:var(--brand-text-muted);">
          <button data-button-id="${BUTTON_IDS.LANG_TOGGLE}" data-locale="en" style="background:none;border:none;color:${locale === 'en' ? 'var(--brand-text)' : '#999'};font-weight:${locale === 'en' ? '600' : '400'};cursor:pointer;">EN</button>
          <span>·</span>
          <button data-button-id="${BUTTON_IDS.LANG_TOGGLE}" data-locale="es" style="background:none;border:none;color:${locale === 'es' ? 'var(--brand-text)' : '#999'};font-weight:${locale === 'es' ? '600' : '400'};cursor:pointer;">ES</button>
        </div>
      </div>`
    : '';

  return `
    <div data-browser-tile="true" data-session-id="${sessionId}" data-locale="${locale}" style="padding:16px;font-family:Inter,-apple-system,BlinkMacSystemFont,sans-serif;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
        <span style="font-size:1.5rem;">🖥️</span>
        <div>
          <div style="font-weight:600;color:var(--brand-text);font-size:0.95rem;">Prest0n Desktop — Live</div>
          <div style="color:#666;font-size:0.78rem;">${goal}</div>
        </div>
      </div>
      ${thumbBlock}
      ${clarifyBlock}
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <a id="${BUTTON_IDS.VIEW_DESKTOP}" data-button-id="${BUTTON_IDS.VIEW_DESKTOP}" href="${fallbackUrl}" target="_blank" rel="noopener"
           style="display:inline-flex;align-items:center;gap:6px;padding:8px 16px;border-radius:8px;background:linear-gradient(135deg,var(--brand-gold),var(--brand-gold-hover));color:var(--brand-charcoal);text-decoration:none;font-size:0.82rem;font-weight:600;">
          🔴 View Desktop
        </a>
      </div>
      <div style="margin-top:10px;font-size:0.7rem;color:var(--brand-text-muted);">Session id: ${sessionId.slice(0, 24)}… · link expires in 5 min</div>
    </div>
  `;
}

function escapeAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// ============================================================================
// Trigger Pattern
// ============================================================================

// Phase 0 hardening: all patterns anchored at start (`^…`); negation guard
// applied via `makeRegexTriggerWithExtractor` so phrases like
// "don't browse to that site, just answer" do not trigger this tool.
const BROWSER_USE_TRIGGER_PATTERNS: RegExp[] = [
  /^browse\s+(.+)/i,
  /^use the computer\s+(?:to\s+)?(.+)/i,
  /^research online\s+(.+)/i,
  /^go to\s+(https?:\/\/.+)/i,
  /^open\s+(https?:\/\/.+)/i,
  /^computer[,:]?\s+(.+)/i,
];

function matchBrowserUseTrigger(text: string): TriggerMatch {
  const trimmed = (text || '').trim();
  if (!trimmed) return { matches: false };
  for (const re of BROWSER_USE_TRIGGER_PATTERNS) {
    const m = trimmed.match(re);
    if (!m || typeof m.index !== 'number') continue;
    if (hasNearbyNegation(trimmed, m.index, m[0].length)) continue;
    return { matches: true, query: (m[1] || '').toLowerCase().trim() };
  }
  return { matches: false };
}

// ============================================================================
// Tool Definition
// ============================================================================

export const browserUseTool: GlobalToolDefinition<BrowserUseState> = {
  id: 'browser-use',
  name: 'Computer Use',
  description: 'Launch a virtual desktop to browse the web in real time',

  theme: BROWSER_USE_THEME,
  buttonIds: BUTTON_IDS,

  triggerPattern: matchBrowserUseTrigger,

  createInitialState: (): BrowserUseState => ({
    goal: '',
    sessionId: null,
    status: 'idle',
  }),

  steps: [
    // ========================================================================
    // Step 1: Launch desktop session
    // ========================================================================
    {
      id: 'launch',
      name: 'Launch Desktop',
      activeDisplayName: 'starting computer',

      execute: async (
        ctx: StepContext<BrowserUseState>,
        rawInput?: unknown
      ): Promise<StepResult> => {
        const input = rawInput as { query?: string } | undefined;
        const goal = input?.query?.trim();

        if (!goal) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'task required' },
            sections: [
              {
                type: 'text',
                content: 'Please include what you\'d like Prest0n to do. For example: "browse h1b visa requirements" or "go to https://bing.com"',
              },
            ],
            theme: 'blue',
          });
          return { status: 'completed', nextStep: null };
        }

        // Owner directive 2026-08-18: computer use is an account feature —
        // signed-out visitors get a polite invitation instead of a session
        // on the shared pool (which also stops anonymous traffic from
        // consuming the capacity signed-in users share).
        const authedUser = getAuth().currentUser;
        if (!authedUser || authedUser.isAnonymous) {
          ctx.messaging.showSpecialMessage({
            header: { title: 'computer use needs an account' },
            sections: [
              {
                type: 'text',
                content:
                  "I'd love to do that for you! Computer use runs on your own dedicated workstation, "
                  + 'which comes with a free Prest0n account — create one and I\'ll set it up for you. '
                  + 'Everything else (questions, research, drafting) works right here without one.',
              },
            ],
            helperText: 'Start your free trial at prest0.ai/start-trial — your workstation is set up automatically.',
            theme: 'blue',
          });
          return { status: 'completed', nextStep: null };
        }

        ctx.setState({ goal, status: 'starting' });

        // Show pending status
        const handle = ctx.messaging.showPending('starting virtual desktop');

        try {
          // Resolve the platform-shared browser-service base URL from Firestore
          // (single source of truth at `system/prest0Core/services/main.browserServiceUrl`).
          const BASE_API = await getBrowserServiceBaseUrl();

          // 2026-08-11 (P0 security item #6): the browser-service now supports
          // a bearer-token gate on all /api/* routes. Resolve the token from
          // the same Firestore SSOT doc (field `browserServiceToken`) and
          // attach it to every /api call below. Deploy-safe: while the token
          // is unprovisioned this resolves to `{}` and requests go out exactly
          // as before, matching the server's allow-all default. /health stays
          // unauthenticated server-side, so the probe needs no header.
          const authHeaders = await getBrowserServiceAuthHeaders();

          // Check if browser-service is healthy
          const healthRes = await fetch(`${BASE_API}/health`, { signal: AbortSignal.timeout(5000) });
          if (!healthRes.ok) {
            handle.finalize({
              payload: {
                kind: 'browser_use.error',
                toolId: 'browser-use',
                severity: 'error',
                data: { errorMessage: 'browser service health check failed', phase: 'health_check' },
                summary: 'browser-use: service unavailable',
              },
              sourceTag: 'browser-use',
            });
            return { status: 'failed', error: new Error('Browser service unavailable') };
          }

          // Capacity is the SERVER's call, not ours (2026-08-09
          // desktop-reliability pass): the old hardcoded `>= 5` pre-check
          // here disagreed with the deployed cap and produced false
          // "slots full" failures. The create call below is authoritative
          // — a genuine cap overflow comes back as an error we map to the
          // friendly capacity payload.

          // Determine URL — if the goal looks like a URL, use it directly
          const urlMatch = goal.match(/^(?:go to |open |visit )?(https?:\/\/\S+)/i);
          const targetUrl = urlMatch ? urlMatch[1] : 'https://www.bing.com';

          // Create (or reuse) the desktop session. No client-minted
          // sessionId — pinning one forces a brand-new session every call
          // and defeats the server's per-user reuse guard, churning toward
          // the cap. A real uid (when signed in) scopes that reuse to this
          // user; 'chat-user' keeps anonymous chat launches sharing one pool.
          const desktopUserId = getAuth().currentUser?.uid || 'chat-user';
          const createRes = await fetch(`${BASE_API}/api/desktop`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
            body: JSON.stringify({
              url: targetUrl,
              userId: desktopUserId,
            }),
          });

          if (!createRes.ok) {
            const errBody = await createRes.json().catch(() => ({ error: 'Unknown error' }));
            const errMsg = (errBody as { error: string }).error;
            if (/maximum desktop sessions/i.test(errMsg)) {
              handle.finalize({
                payload: {
                  kind: 'browser_use.error',
                  toolId: 'browser-use',
                  severity: 'warning',
                  data: {
                    errorMessage: 'all desktop slots are in use',
                    phase: 'capacity',
                    manageUrl: '/desktop',
                  },
                  summary: 'browser-use: max desktop sessions reached',
                },
                sourceTag: 'browser-use',
              });
              return { status: 'failed', error: new Error('Max desktop sessions reached') };
            }
            handle.finalize({
              payload: {
                kind: 'browser_use.error',
                toolId: 'browser-use',
                severity: 'error',
                data: { errorMessage: errMsg, phase: 'session_create' },
                summary: 'browser-use: failed to start virtual desktop',
              },
              sourceTag: 'browser-use',
            });
            return { status: 'failed', error: new Error(errMsg) };
          }

          const data = await createRes.json() as { sessionId: string; wsPort: number; display: string };
          ctx.setState({ sessionId: data.sessionId, status: 'running' });

          // Phase 2: mint a signed deep-link (5-min JWT) and render the
          // inline browser tile (live thumbnail + View Desktop button).
          let signedUrl: string | null = null;
          try {
            const link = await mintDeepLinkUrl(data.sessionId);
            signedUrl = link.url;
          } catch {
            signedUrl = null;
          }

          // Try to fetch the latest frame from the WS cache (it may not
          // be available yet on first paint; the page itself will
          // re-render when frames arrive).
          let thumbnailDataUrl: string | null = null;
          try {
            thumbnailDataUrl = getInlineBrowserTileFrame(data.sessionId);
          } catch {
            thumbnailDataUrl = null;
          }

          // Phase 1.2 form-aware: poll form-state once. If the planner
          // has already paused on a missing required field, surface the
          // EN/ES clarifying question inline instead of just a thumbnail.
          let pendingFieldId: string | null = null;
          let pendingClarifyingQuestion: { en: string; es: string } | null = null;
          try {
            const formStateRes = await fetch(
              `${BASE_API}/api/sessions/${encodeURIComponent(data.sessionId)}/form-state`,
              { signal: AbortSignal.timeout(2500), headers: authHeaders }
            );
            if (formStateRes.ok) {
              const fs = (await formStateRes.json()) as {
                paused?: boolean;
                pendingFieldId?: string | null;
                pendingClarifyingQuestion?: { en: string; es: string } | null;
              };
              if (fs.paused) {
                pendingFieldId = fs.pendingFieldId ?? null;
                pendingClarifyingQuestion = fs.pendingClarifyingQuestion ?? null;
                ctx.setState({
                  pendingFieldId,
                  pendingClarifyingQuestion,
                });
              }
            }
          } catch {
            // form-aware mode might be off — that's fine.
          }

          const resultHtml = renderBrowserTileHtml({
            sessionId: data.sessionId,
            goal,
            signedUrl,
            thumbnailDataUrl,
            pendingFieldId,
            pendingClarifyingQuestion,
          });

          handle.finalize({ text: `🖥️ Virtual desktop started: ${goal}`, html: resultHtml, sourceTag: 'browser-use' });

          return {
            status: 'completed',
            nextStep: null,
            carryForward: { sessionId: data.sessionId },
          };

        } catch (err) {
          const message = err instanceof Error ? err.message : String(err);
          handle.finalize({
            payload: {
              kind: 'browser_use.error',
              toolId: 'browser-use',
              severity: 'error',
              data: { errorMessage: message, phase: 'session_setup' },
              summary: 'browser-use: error during session setup',
            },
            sourceTag: 'browser-use',
          });
          return { status: 'failed', error: err instanceof Error ? err : new Error(message) };
        }
      },
    },
  ],

};

export default browserUseTool;
